import { getConfigProperty } from '@shared/pipes/config'
import { isClient } from '@shared/utils/is-client'

type NormalizedError = {
	name: string
	message: string
	stack?: string
}

let lastLoggedKey: string | null = null

const normalizeError = (error: unknown): NormalizedError => {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			stack: error.stack,
		}
	}

	if (typeof error === 'string') {
		return { name: 'Error', message: error }
	}

	if (error && typeof error === 'object') {
		const obj = error as Record<string, unknown>
		const message =
			typeof obj.message === 'string'
				? obj.message
				: typeof obj.reason === 'string'
					? obj.reason
					: JSON.stringify(error)

		return {
			name: typeof obj.name === 'string' ? obj.name : 'Error',
			message,
			stack: typeof obj.stack === 'string' ? obj.stack : undefined,
		}
	}

	return { name: 'Error', message: String(error) }
}

const getEnvContext = (): Record<string, string | undefined> => {
	if (!isClient) {
		return {}
	}

	return {
		mode: getConfigProperty('MODE'),
		ui_version: getConfigProperty('UI_VERSION'),
		url: typeof window !== 'undefined' ? window.location.href : undefined,
	}
}

const buildDedupeKey = (normalized: NormalizedError, source?: unknown): string => {
	return `${String(source)}|${normalized.name}|${normalized.message}|${normalized.stack ?? ''}`
}

export const logAppError = (error: unknown, context?: Record<string, unknown>): void => {
	const normalized = normalizeError(error)
	const dedupeKey = buildDedupeKey(normalized, context?.source)

	if (dedupeKey === lastLoggedKey) {
		return
	}

	lastLoggedKey = dedupeKey

	console.groupCollapsed('[app-error]')
	console.error(normalized.message)
	console.error('error:', normalized)
	if (context) {
		console.error('context:', { ...getEnvContext(), ...context })
	} else {
		console.error('context:', getEnvContext())
	}
	console.groupEnd()
}

export const shouldIgnoreGlobalError = (message: string): boolean => {
	if (message === 'Script error.') {
		return true
	}

	if (/request aborted/i.test(message)) {
		return true
	}

	if (/Non-Error exception captured/i.test(message)) {
		return true
	}

	return false
}
